// Wall-clock marks for a single-file import, handed from the import sheet to
// the transcript page so the wait the person actually sat through can be
// reported once the text is on screen. Memory only: a reload ends that wait,
// and a transcript opened later from the list is not a wait at all.

type ImportMarks = {
  /** Files chosen in the picker. */
  selectedAt: number;
  /** Upload finished; the server has the file. */
  uploadedAt: number;
  sizeMb: number;
};

/**
 * What explains a slow upload, without the file name: its size, container,
 * and the network the browser reports (Chromium only; Safari omits it).
 */
export const describeUpload = (
  file: { name: string; size: number },
  effectiveType?: string,
): { sizeMb: number; format: string; network?: string } => ({
  sizeMb: Math.round((file.size / 1_048_576) * 10) / 10,
  format: /\.([a-z0-9]{1,8})$/i.exec(file.name)?.[1]?.toLowerCase() || "unknown",
  ...(effectiveType ? { network: effectiveType } : {}),
});

const _pending = new Map<string, ImportMarks>();

export const recordImportTiming = (
  transcriptId: string,
  marks: ImportMarks,
): void => {
  _pending.set(transcriptId, marks);
};

/**
 * The waits behind a transcript that just became readable, reported once;
 * null when this session did not upload it.
 */
export const takeImportWaits = (
  transcriptId: string,
  now: number,
): { transcriptionMs: number; totalMs: number; sizeMb: number } | null => {
  const marks = _pending.get(transcriptId);
  if (!marks) return null;
  _pending.delete(transcriptId);
  return {
    transcriptionMs: Math.max(0, now - marks.uploadedAt),
    totalMs: Math.max(0, now - marks.selectedAt),
    sizeMb: marks.sizeMb,
  };
};
